import Vue from 'vue';
import BootstrapVue from 'bootstrap-vue';
import VueJsonPretty from 'vue-json-pretty';
import VueLogger from 'vuejs-logger';

import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';

import Keycloak from 'keycloak-js';

import get from 'lodash.get';
import round from 'lodash.round';
import unescape from 'lodash.unescape';

import App from './App.vue';
import router from './router';
import store from './store';
import http from './api';

import '@fs/date-picker/dist/date-picker.css';

import vuetify from './plugins/vuetify';
import i18n from './plugins/i18n';
import { moment, momentFilter } from './plugins/moment';

import stickyColumns from './directives/sticky-columns';

import config from './config';

const styles = {
  aurus: () => import('@/assets/styles/aurus.scss'),
  sollers: () => import('@/assets/styles/sollers.scss'),
  argo: () => import('@/assets/styles/argo.scss'),
  uaz: () => import('@/assets/styles/uaz.scss'),
};

styles[config.styleName]();

Vue.use(BootstrapVue);

const isProduction = process.env.NODE_ENV === 'production';

const loggerOptions = {
  isEnabled: !isProduction || !!config.logLevel,
  logLevel: isProduction ? 'error' : 'debug',
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: false,
  separator: '|',
  showConsoleColors: true,
};

Vue.use(VueLogger, loggerOptions);

const logTime = new Intl.DateTimeFormat('ru', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: '3',
});

Object.keys(Vue.$log).forEach((key) => {
  if (typeof Vue.$log[key] === 'function') {
    const orig = Vue.$log[key];
    Vue.$log[key] = (...args) => orig(logTime.format(new Date()), ...args);
  }
});

Vue.moment = moment;
Object.defineProperties(Vue.prototype, { $moment: { get() { return moment; } } });
Vue.filter('moment', momentFilter);

Vue.component('VueJsonPretty', VueJsonPretty);

Vue.directive('sticky-columns', stickyColumns);

Vue.prototype.$config = config;
Vue.prototype.$http = http;
Vue.prototype.$utils = { get, round, unescape };

Vue.config.productionTip = false;

if (config.SENTRY_DSN) {
  Vue.prototype.$sentry = Sentry;

  Sentry.init({
    Vue,
    dsn: config.SENTRY_DSN,
    ...(config.SENTRY_TUNNEL ? { tunnel: config.SENTRY_TUNNEL } : {}),
    ignoreErrors: [/^Token Expired$/],
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    logErrors: true,
  });
}

const $keycloak = {
  config: config.SSO_FRONT_CONFIG ? JSON.parse(config.SSO_FRONT_CONFIG) : null,
  inited: false,
  realms: [],
  adapters: {},
  async initRealm(realm) {
    const adapter = this.adapters[realm];

    if (!adapter) { return null; }

    try {
      await adapter.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframeInterval: 1,
        enableLogging: true,
      });
      return adapter.authenticated;
    } catch (e) {
      // realms.splice(realms.findIndex((elm) => (elm.realm === realm)), 1);
      return null;
    }
  },
};

Vue.prototype.$keycloak = $keycloak;

if ($keycloak.config) {
  $keycloak.config.forEach((realmConfig) => {
    $keycloak.adapters[realmConfig.realm] = new Keycloak(realmConfig);
  });
}

const app = (async () => {
  Vue.$log.debug('start');
  let ssoAuthenticated = false;

  const realm = localStorage.getItem('realm');
  if (realm) {
    Vue.$log.debug('realm: ', realm);
    const adapter = $keycloak.adapters[realm];
    if (adapter) {
      Vue.$log.debug('match adapter');
      try {
        ssoAuthenticated = await $keycloak.initRealm(realm);

        if (ssoAuthenticated) {
          Vue.$log.debug('Authenticated');
          localStorage.setItem('jwtacc', adapter.token);
        } else {
          Vue.$log.debug('Not authenticated');
          localStorage.removeItem('jwtacc');
        }
      } catch (err) {
        Vue.$log.debug('Error init realm', err);
        localStorage.removeItem('realm');
        localStorage.removeItem('jwtacc');
      }
    } else {
      Vue.$log.debug('Unknwon realm', realm);
      localStorage.removeItem('realm');
    }
  }

  const jwtacc = localStorage.getItem('jwtacc');
  if (jwtacc) {
    try {
      await store.dispatch('performAuth', { jwtacc, app: Vue.prototype });
    } catch (err) {
      if (ssoAuthenticated) {
        const { data = {} } = err.response || {};

        const errorUrl = data.name && data.type ? `${data.name}.${data.type}` : (data.message || err.message);
        Vue.$log.debug(err.message, errorUrl);
        window.history.replaceState(null, '', `/sign-in/not-registred?error=${errorUrl}`);
      } else {
        localStorage.removeItem('jwtacc');
      }
    }
  }

  Vue.prototype.$toast = function $toast(message, options = {}) {
    this.$bvToast.toast(unescape(message), {
      title: this.$t('toast.success'),
      variant: 'success',
      solid: true,
      autoHideDelay: 15000,
      ...options,
    });
  };

  Vue.$log.debug('Vue init ---');
  const ref = new Vue({
    el: '#app',
    store,
    router,
    i18n,
    vuetify,
    render: (h) => h(App),
  });

  http.$app = ref;

  return ref;
})();

export default app;
